import { AssetIcon } from 'assets/icons/AssetIcon';
import useConditionalTimeout from 'beautiful-react-hooks/useConditionalTimeout';
import cx from 'classnames';
import Loader from 'components/loader/Loader';
import PopoverBox from 'components/popover/PopoverBox';
import Typography, { TypographyColor } from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import {
  THEME_LIVEVIEW_WRAPPER_SCALE,
  TIME_SHOW_POPOVER_GUIDE_DURATION,
  TIME_TO_SHOW_PHOTO,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { includesId } from 'helpers/array.helper';
import { isEqualVal } from 'helpers/string.helper';
import { liveViewUrl, processedImageUrl } from 'helpers/url.helper';
import { useTranslation } from 'hooks/useTranslation';
import React, { useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'store/store-hooks';
import { useShootingContext } from './ShootingContext';
import ShowThemeOnArea from './ShowThemeOnArea';
import { useShootingActions } from './useShootingActions';

interface ShootingLiveViewProps {
  triggerShowTheme: boolean;
}

const ShootingLiveView: React.FC<ShootingLiveViewProps> = () => {
  const liveviewRef = useRef<HTMLDivElement>(null);
  const btnSelectRetakeRef = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const {
    transactionId,
    wasRetakePhoto,
    isShootingOfRetakingPhoto,
    isRetakePhoto,
  } = useAppSelector((s) => s.selfeApp);
  const { isVerticalScreen, isTakePhotoOrigin } = useAppSelector(
    (s) => s.booth,
  );
  const { listPhotoTakens, currentLayout } = useBoothAppContext();
  const {
    listIndexOfRetake,
    takingPhotoIndex,
    isWaitingApiShooting,
    isFlashCamera,
    currentCameraId,
    selectImageIndex,
    imageHash,
    sizeOfNeedToTakePhoto,
    numberOfTakePicture,
    isDisplayOnLiveview,
  } = useShootingContext();
  const { handleToggleRetakePhoto } = useShootingActions();

  const [isShowBtnRetakeRect, setIsShowBtnRetakeRect] = useState(false);
  const [liveHash] = useState(Date.now());

  // Show kết quả chụp ảnh
  useConditionalTimeout(
    () => {
      setIsShowBtnRetakeRect(true);
    },
    TIME_TO_SHOW_PHOTO,
    !isRetakePhoto && sizeOfNeedToTakePhoto >= numberOfTakePicture,
  );

  // Close popover guide
  useConditionalTimeout(
    () => setIsShowBtnRetakeRect(false),
    TIME_SHOW_POPOVER_GUIDE_DURATION,
    isShowBtnRetakeRect,
  );

  const scaleOfWebcamFrameWrapper = useMemo(
    () => (isDisplayOnLiveview ? THEME_LIVEVIEW_WRAPPER_SCALE : 1),
    [isDisplayOnLiveview],
  );

  return (
    <>
      <div
        className={cx('webcam-frame', {
          'webcam-frame-3per2':
            isEqualVal(currentLayout?.ratioX, 3) &&
            isEqualVal(currentLayout?.ratioY, 2),
          'webcam-frame-5per4':
            isEqualVal(currentLayout?.ratioX, 5) &&
            isEqualVal(currentLayout?.ratioY, 4),
          'webcam-frame-4per3':
            isEqualVal(currentLayout?.ratioX, 4) &&
            isEqualVal(currentLayout?.ratioY, 3),
        })}
      >
        <div
          className="webcam-frame-wrapper"
          ref={liveviewRef}
          style={{
            aspectRatio: `${currentLayout?.ratioX || 1} / ${
              currentLayout?.ratioY || 1
            }`,
            transform: `scale(${scaleOfWebcamFrameWrapper})`,
          }}
        >
          {/* Hiển thị theme overlay */}
          {isDisplayOnLiveview && (
            <ShowThemeOnArea
              liveviewSize={liveviewRef?.current?.getBoundingClientRect()}
              takingPhotoIndex={takingPhotoIndex}
              isShowInLiveView
            />
          )}
          {/* Flash hoặc chờ API */}
          {(isTakePhotoOrigin ? isWaitingApiShooting : isFlashCamera) && (
            <Loader className="freeze-frame" spin={<></>} loading />
          )}
          {/* Liveview */}
          <img
            src={liveViewUrl(currentCameraId, currentLayout?.ratio, liveHash)}
            alt="camera"
          />
          {/* Kết quả ảnh retake */}
          {isRetakePhoto && !isShootingOfRetakingPhoto && (
            <>
              {listPhotoTakens?.data?.[selectImageIndex] && (
                <img
                  className="retake-photo-img-result"
                  src={processedImageUrl(
                    transactionId,
                    listPhotoTakens?.data?.[selectImageIndex],
                    imageHash,
                  )}
                  alt="result img"
                />
              )}
              <button
                ref={btnSelectRetakeRef}
                type="button"
                className={cx('btn-retake-a-photo', {
                  invisible: wasRetakePhoto,
                })}
                onClick={handleToggleRetakePhoto}
                disabled={wasRetakePhoto}
              >
                <AssetIcon.RefreshIcon />
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.SMALL}
                  className="text-retake-a-photo"
                >
                  {includesId(listIndexOfRetake, selectImageIndex)
                    ? t('page:noRetakeAPhoto')
                    : t('page:retakeAPhoto')}
                </Typography>
              </button>
            </>
          )}
        </div>
      </div>

      <PopoverBox
        className="pb-popover-shooting-page pb-popover-shooting-page-2"
        anchorDimension={btnSelectRetakeRef?.current?.getBoundingClientRect?.()}
        popoverDir={isVerticalScreen ? 'bottom' : 'left'}
        content={`${t('page:selectRetakeGuideInfo')}`}
        open={isShowBtnRetakeRect}
      />
    </>
  );
};

export default React.memo(ShootingLiveView);
